import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Trash2, Search, FileText, Eye, X, Package, Globe2, PlaneLanding, MapPin } from "lucide-react";
import { format } from "date-fns";

const PricelistListPage = () => {
  const [profiles, setProfiles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  
  // STATE BARU UNTUK TAB GROUPING
  const [activeTab, setActiveTab] = useState("EXPORT");

  // STATE UNTUK MODAL DETAIL
  const [selectedProfile, setSelectedProfile] = useState(null); 
  const [detailRates, setDetailRates] = useState([]); 
  const [viewMode, setViewMode] = useState("DOC"); 
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);

  const API_URL = "/api";

  useEffect(() => {
    fetchProfiles();
  }, []);

  const fetchProfiles = async () => {
    try {
      setIsLoading(true);
      const res = await axios.get(`${API_URL}/pricelists/profiles`);
      setProfiles(res.data);
    } catch (error) {
      toast.error("Gagal mengambil data pricelist");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Yakin ingin menghapus pricelist "${name}"? Semua harga di dalamnya akan ikut terhapus permanen!`)) return;
    try {
      await axios.delete(`${API_URL}/pricelists/profiles/${id}`);
      toast.success("Pricelist berhasil dihapus!");
      fetchProfiles();
    } catch (error) {
      console.error(error);
      toast.error("Gagal menghapus pricelist. Pastikan backend sudah diupdate.");
    }
  };

  const handleViewDetail = async (profile) => {
    setSelectedProfile(profile);
    setViewMode("DOC"); 
    setIsLoadingDetail(true);
    try {
      const res = await axios.get(`${API_URL}/pricelists/profiles/${profile.id}/rates`);
      setDetailRates(res.data);
    } catch (error) {
      toast.error("Gagal mengambil detail harga");
    } finally {
      setIsLoadingDetail(false);
    }
  };

  const closeDetail = () => {
    setSelectedProfile(null);
    setDetailRates([]);
  };

  // --- LOGIKA MENYUSUN TABEL MATRIX ---
  const processMatrixData = () => {
    const filtered = detailRates.filter(r => r.item_type === viewMode);
    
    const rows = {};
    const zones = new Set(); 

    filtered.forEach(item => {
        if (!rows[item.weight]) {
            rows[item.weight] = { 
                weight: item.weight, 
                is_multiplier: item.is_multipler,
                prices: {} 
            };
        }
        rows[item.weight].prices[item.zone] = item.price;
        zones.add(item.zone);
    });

    const sortedZones = Array.from(zones).sort();
    const sortedRows = Object.values(rows).sort((a, b) => a.weight - b.weight);

    return { sortedZones, sortedRows };
  };

  const { sortedZones, sortedRows } = processMatrixData();

  // FUNGSI FILTER BERDASARKAN TAB & SEARCH
  const filteredProfiles = profiles.filter(p => 
    p.service_type === activeTab &&
    p.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // FUNGSI PINTAR PEWARNA BADGE LAYANAN
  const getBadgeStyle = (type) => {
      if (type === 'IMPORT') return 'bg-orange-100 text-orange-700 border-orange-200';
      if (type === 'DOMESTIC') return 'bg-purple-100 text-purple-700 border-purple-200';
      return 'bg-blue-100 text-blue-700 border-blue-200'; // Default EXPORT
  };

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      
      {/* HEADER PAGE */}
      <div className="flex flex-col md:flex-row justify-between items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Daftar Pricelist</h1>
          <p className="text-gray-500">Kelola master harga untuk Export, Import, dan Domestic.</p>
        </div>
        <div className="relative w-full md:w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
          <input 
            type="text" 
            placeholder="Cari pricelist..." 
            className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* --- TABS GROUPING BARU --- */}
      <div className="flex bg-gray-100 p-1.5 rounded-2xl w-full max-w-2xl">
        <button 
          onClick={() => setActiveTab("EXPORT")}
          className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl font-bold transition-all ${activeTab === "EXPORT" ? "bg-white text-blue-600 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
        >
          <Globe2 size={18}/> EXPORT
        </button>
        <button 
          onClick={() => setActiveTab("IMPORT")}
          className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl font-bold transition-all ${activeTab === "IMPORT" ? "bg-white text-orange-600 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
        >
          <PlaneLanding size={18}/> IMPORT
        </button>
        <button 
          onClick={() => setActiveTab("DOMESTIC")}
          className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl font-bold transition-all ${activeTab === "DOMESTIC" ? "bg-white text-purple-600 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
        >
          <MapPin size={18}/> DOMESTIC
        </button>
      </div>

      {/* TABLE LIST PROFILE */}
      <div className="bg-white border rounded-xl shadow-sm overflow-hidden">
        <table className="w-full text-left">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="px-6 py-4 text-sm font-semibold text-gray-700">Nama Pricelist</th>
              <th className="px-6 py-4 text-sm font-semibold text-gray-700">Tipe Layanan</th>
              <th className="px-6 py-4 text-sm font-semibold text-gray-700">Tanggal Dibuat</th>
              <th className="px-6 py-4 text-sm font-semibold text-gray-700 text-right">Aksi</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {isLoading ? (
              <tr><td colSpan="4" className="px-6 py-8 text-center text-gray-500">Loading...</td></tr>
            ) : filteredProfiles.length === 0 ? (
              <tr><td colSpan="4" className="px-6 py-8 text-center text-gray-500">Tidak ada data pricelist di Tab ini.</td></tr>
            ) : (
              filteredProfiles.map((profile) => (
                <tr key={profile.id} className="hover:bg-gray-50 transition">
                  <td className="px-6 py-4 font-bold text-gray-900">{profile.name}</td>
                  <td className="px-6 py-4">
                    <span className={`px-3 py-1 text-[11px] font-black rounded-full uppercase tracking-wider border ${getBadgeStyle(profile.service_type)}`}>
                        {profile.service_type}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500 font-mono">
                    {profile.created_at ? format(new Date(profile.created_at), "dd MMM yyyy") : "-"}
                  </td>
                  <td className="px-6 py-4 text-right space-x-2">
                    <button 
                      onClick={() => handleViewDetail(profile)}
                      className="text-blue-600 hover:bg-blue-50 p-2 rounded-lg transition"
                      title="Lihat Harga"
                    >
                      <Eye size={18} />
                    </button>
                    <button 
                      onClick={() => handleDelete(profile.id, profile.name)}
                      className="text-red-600 hover:bg-red-50 p-2 rounded-lg transition"
                      title="Hapus"
                    >
                      <Trash2 size={18} />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* --- MODAL POPUP DETAIL --- */}
      {selectedProfile && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl h-[85vh] flex flex-col overflow-hidden">
            
            {/* Modal Header */}
            <div className="p-5 border-b flex justify-between items-center bg-gray-50">
              <div>
                <div className="flex items-center gap-3">
                    <h2 className="text-xl font-bold text-gray-900">{selectedProfile.name}</h2>
                    <span className={`px-2 py-0.5 text-[10px] font-bold rounded-md border ${getBadgeStyle(selectedProfile.service_type)}`}>
                        {selectedProfile.service_type}
                    </span>
                </div>
                <p className="text-sm text-gray-500 mt-1">Detail matriks tarif pengiriman</p>
              </div>
              <button onClick={closeDetail} className="text-gray-400 hover:text-red-600 p-2 rounded-lg bg-gray-100 hover:bg-red-50 transition">
                <X size={20} />
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 flex flex-col p-6 overflow-hidden">
              
              {/* Tabs Document vs Parcel */}
              <div className="flex gap-4 mb-4 border-b pb-4">
                <button 
                    onClick={() => setViewMode("DOC")}
                    className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-bold transition shadow-sm ${viewMode === "DOC" ? "bg-blue-600 text-white" : "bg-white border text-gray-600 hover:bg-gray-50"}`}
                >
                    <FileText size={18} /> Document (DOX)
                </button>
                <button 
                    onClick={() => setViewMode("PARCEL")}
                    className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-bold transition shadow-sm ${viewMode === "PARCEL" ? "bg-blue-600 text-white" : "bg-white border text-gray-600 hover:bg-gray-50"}`}
                >
                    <Package size={18} /> Non-Document (SPX)
                </button>
              </div>

              {/* Table Data */}
              <div className="flex-1 overflow-auto border-2 rounded-xl shadow-inner">
                {isLoadingDetail ? (
                    <div className="flex flex-col items-center justify-center h-full text-gray-500 space-y-3">
                        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                        <p className="font-bold animate-pulse">Memuat data harga...</p>
                    </div>
                ) : sortedRows.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-gray-400">
                        <FileText size={48} className="mb-2 opacity-20"/>
                        <p>Tidak ada data harga untuk kategori ini.</p>
                    </div>
                ) : (
                    <table className="w-full text-left text-sm whitespace-nowrap">
                        <thead className="bg-gray-100 sticky top-0 z-10 shadow-sm border-b-2">
                            <tr>
                                <th className="px-5 py-4 font-black text-gray-800 bg-gray-200 sticky left-0 z-20 border-r-2 shadow-sm">Weight (Kg)</th>
                                {sortedZones.map(zone => (
                                    <th key={zone} className="px-5 py-4 font-black text-center text-gray-800 bg-gray-100 border-r border-gray-200">
                                        Zone {zone}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {sortedRows.map((row, idx) => (
                                <tr key={idx} className={row.is_multiplier ? "bg-yellow-50 hover:bg-yellow-100" : "hover:bg-blue-50/30"}>
                                    <td className="px-5 py-3 font-bold bg-white sticky left-0 border-r-2 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)] z-10 text-gray-800">
                                        {row.weight} {row.is_multiplier && <span className="text-[10px] text-yellow-700 bg-yellow-200 px-1.5 py-0.5 rounded ml-2">/ Kg</span>}
                                    </td>
                                    {sortedZones.map(zone => (
                                        <td key={zone} className="px-5 py-3 text-center border-r border-gray-100 font-mono text-gray-700">
                                            {row.prices[zone] ? `Rp ${row.prices[zone].toLocaleString('id-ID')}` : <span className="text-gray-300">-</span>}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
              </div>
              
              <div className="mt-4 text-xs font-bold text-gray-400 text-right bg-gray-50 p-2 rounded-lg border inline-block ml-auto">
                * Tanda <span className="text-yellow-700 bg-yellow-100 px-1 rounded mx-1">/ Kg</span> berarti harga per kilogram (Multiplier) untuk Overweight.
              </div>
            </div>

          </div>
        </div>
      )}

    </div>
  );
};

export default PricelistListPage;